import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { MessageSquare, Ticket as TicketIcon, UserCheck } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { TICKET_TRANSITIONS, type TicketStatus } from '@aeris/communication'
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Page,
  PageHeader,
  Section,
  Select,
  Spinner,
  Textarea,
} from '@aeris/ui'

import { PriorityBadge, StatusBadge } from '../components/ticket-badges.tsx'
import {
  addTicketNote,
  ApiError,
  assignTicket,
  changeTicketStatus,
  fetchSalespeople,
  fetchTicket,
  type TicketEventType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { EVENT_META, STATUS_META, formatDateTime } from '../lib/ticket-meta.tsx'

/**
 * Chamados → um chamado (F061): o detalhe vira ROTA, `/chamados/$id`.
 *
 * Era um diálogo `max-w-2xl` sem URL. O custo concreto: não dava para mandar
 * um chamado para um colega por link — a única forma de chegar nele era abrir
 * a lista e achar a linha. Um chamado é exatamente o tipo de coisa que se
 * passa adiante ("dá uma olhada no #142"), e o protocolo já existe desde a
 * F051 justamente para ser citado.
 *
 * ## O `window.prompt` que sobrou até aqui
 *
 * Fechar um chamado pedia o motivo por `window.prompt()` — um diálogo do
 * NAVEGADOR, que não pode ser estilizado, trava a aba inteira, não aparece em
 * teste automatizado e some por completo dentro do Modo PC (Tauri). O
 * `ConfirmDialog` do design system foi construído com um campo `reason`
 * exatamente para substituí-lo, e não tinha nenhum consumidor.
 */

export const ChamadosDetalhePage = (): ReactNode => {
  const { id } = useParams({ from: '/shell/chamados/$id' })
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canWrite = allows('communication.ticket.write')

  const [actionError, setActionError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [assignee, setAssignee] = useState('')
  /** A transição esperando confirmação — só `closed` a pede. */
  const [closing, setClosing] = useState(false)

  const detail = useQuery({
    queryKey: ['communication-ticket', id],
    queryFn: async ({ signal }) => await fetchTicket(id, signal),
  })

  const salespeople = useQuery({
    queryKey: ['team-salespeople'],
    queryFn: async ({ signal }) => await fetchSalespeople(signal),
  })

  const employeeNames = new Map(
    (salespeople.data?.items ?? []).map((person) => [
      person.userId,
      person.displayName,
    ])
  )

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['communication-ticket', id],
    })
    await queryClient.invalidateQueries({ queryKey: ['communication-tickets'] })
  }

  const transition = useMutation({
    mutationFn: async (input: {
      status: TicketStatus
      note: string | null
    }) => {
      if (detail.data === undefined) return
      await changeTicketStatus(id, {
        status: input.status,
        note: input.note,
        version: detail.data.version,
      })
    },
    onSuccess: async () => {
      setActionError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      setActionError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível mudar a situação deste chamado.'
      )
    },
  })

  const assign = useMutation({
    mutationFn: async (employeeId: string | null) => {
      if (detail.data === undefined) return
      await assignTicket(id, { employeeId, version: detail.data.version })
    },
    onSuccess: async () => {
      setActionError(null)
      setAssignee('')
      await invalidate()
    },
    onError: (cause: unknown) => {
      setActionError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível atribuir este chamado.'
      )
    },
  })

  const addNote = useMutation({
    mutationFn: async (value: string) =>
      await addTicketNote(id, { note: value }),
    onSuccess: async () => {
      setActionError(null)
      setNote('')
      await invalidate()
    },
    onError: (cause: unknown) => {
      setActionError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível acrescentar esta nota.'
      )
    },
  })

  const ticket = detail.data
  const nextStatuses =
    ticket === undefined ? [] : TICKET_TRANSITIONS[ticket.status]

  const backLink = (
    <Link to="/chamados" className="text-(--brand-primary) hover:underline">
      ← Chamados
    </Link>
  )

  if (detail.isPending) {
    return (
      <Page>
        <PageHeader eyebrow={backLink} title="Chamado" />
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Page>
    )
  }

  if (detail.isError || ticket === undefined) {
    return (
      <Page>
        <PageHeader eyebrow={backLink} title="Chamado" />
        <EmptyState
          icon={<TicketIcon aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar este chamado"
          description="Ele pode ter sido apagado, ou o link está errado."
        />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        eyebrow={backLink}
        title={`Chamado ${ticket.number === null ? '' : `#${String(ticket.number)}`}`}
        description={ticket.subject}
      />

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={ticket.status} />
        <PriorityBadge priority={ticket.priority} />
        {ticket.assigneeEmployeeId === null ? (
          <span className="text-(length:--text-xs) text-(--text-muted)">
            Sem responsável
          </span>
        ) : (
          <Badge
            tone="info"
            icon={<UserCheck aria-hidden="true" className="h-3 w-3" />}
          >
            {employeeNames.get(ticket.assigneeEmployeeId) ?? 'Atribuído'}
          </Badge>
        )}
      </div>

      {ticket.description === null ? null : (
        <p className="text-(length:--text-sm) whitespace-pre-wrap text-(--text-secondary)">
          {ticket.description}
        </p>
      )}

      {canWrite ? (
        <Section
          title="Ações"
          description="Toda mudança daqui entra no histórico abaixo, com quem fez e quando."
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-(length:--text-sm) font-(--weight-medium) text-(--text-secondary)">
              Mudar para:
            </span>
            {nextStatuses.length === 0 ? (
              <span className="text-(length:--text-sm) text-(--text-muted)">
                Nenhuma transição disponível
              </span>
            ) : (
              nextStatuses.map((status) => (
                <Button
                  key={status}
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={transition.isPending}
                  onClick={() => {
                    // Fechar pede o motivo; as outras transições não têm o que
                    // perguntar, e um diálogo a mais em cada clique seria
                    // ruído que ensina a clicar sem ler.
                    if (status === 'closed') {
                      setClosing(true)
                      return
                    }
                    transition.mutate({ status, note: null })
                  }}
                >
                  {STATUS_META[status].label}
                </Button>
              ))
            )}
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <Field label="Atribuir a" className="min-w-56 flex-1">
              {(field) => (
                <Select
                  {...field}
                  value={assignee}
                  onChange={(event) => {
                    setAssignee(event.target.value)
                  }}
                  disabled={salespeople.isPending}
                >
                  <option value="">Escolha uma pessoa</option>
                  {(salespeople.data?.items ?? []).map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button
              type="button"
              variant="secondary"
              disabled={assignee === '' || assign.isPending}
              onClick={() => {
                assign.mutate(assignee)
              }}
            >
              Atribuir
            </Button>
            {ticket.assigneeEmployeeId === null ? null : (
              <Button
                type="button"
                variant="ghost"
                disabled={assign.isPending}
                onClick={() => {
                  assign.mutate(null)
                }}
              >
                Tirar responsável
              </Button>
            )}
          </div>
        </Section>
      ) : null}

      {actionError === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {actionError}
        </p>
      )}

      <Section title="Histórico">
        {ticket.events.length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nenhum evento ainda.
          </p>
        ) : (
          <ol className="flex flex-col gap-2">
            {ticket.events.map((event: TicketEventType) => (
              <li
                key={event.id}
                className="flex items-start gap-2 rounded-(--radius-md) border border-(--border-default) px-3 py-2 text-(length:--text-sm)"
              >
                <span className="mt-0.5 text-(--text-muted)">
                  {EVENT_META[event.kind]?.icon ?? (
                    <MessageSquare aria-hidden="true" className="h-3.5 w-3.5" />
                  )}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-(--weight-medium) text-(--text-primary)">
                      {EVENT_META[event.kind]?.label ?? event.kind}
                    </span>
                    {event.employeeId === null ? null : (
                      <span className="text-(length:--text-xs) text-(--text-muted)">
                        {employeeNames.get(event.employeeId) ?? 'alguém'}
                      </span>
                    )}
                    <span className="text-(length:--text-xs) text-(--text-muted)">
                      {formatDateTime(event.createdAt)}
                    </span>
                  </div>
                  {event.note === null ? null : (
                    <p className="whitespace-pre-wrap text-(--text-secondary)">
                      {event.note}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}

        {canWrite ? (
          <form
            className="flex flex-wrap items-end gap-2 border-t border-(--border-default) pt-3"
            onSubmit={(event) => {
              event.preventDefault()
              if (note.trim() === '') return
              addNote.mutate(note.trim())
            }}
          >
            <Field label="Nova nota" className="min-w-56 flex-1">
              {(field) => (
                <Textarea
                  {...field}
                  value={note}
                  onChange={(event) => {
                    setNote(event.target.value)
                  }}
                  rows={2}
                  placeholder="O que aconteceu, ou o que falta fazer…"
                />
              )}
            </Field>
            <Button
              type="submit"
              variant="primary"
              disabled={note.trim() === '' || addNote.isPending}
              loading={addNote.isPending}
            >
              Adicionar
            </Button>
          </form>
        ) : null}
      </Section>

      <ConfirmDialog
        open={closing}
        onOpenChange={setClosing}
        tone="neutral"
        title={`Fechar o chamado ${ticket.number === null ? '' : `#${String(ticket.number)}`}?`}
        description="Ele sai da lista de trabalho, e o motivo entra no histórico. Um chamado fechado ainda pode ser reaberto."
        confirmLabel="Fechar chamado"
        reason={{
          label: 'Motivo do fechamento (opcional)',
          placeholder: 'Resolvido no balcão, cliente retirou a peça…',
        }}
        loading={transition.isPending}
        onConfirm={(reason) => {
          setClosing(false)
          transition.mutate({
            status: 'closed',
            note:
              reason === undefined || reason.trim() === ''
                ? null
                : reason.trim(),
          })
        }}
      />
    </Page>
  )
}
